import axios from 'axios';
import { environment } from '../../environments/environment';

export interface ValidationResponse {
    is_valid: boolean;
    message: string;
    missing_fields?: string[];
}

export interface ValidationError {
    profile_incomplete: string;
    missing_fields: string[];
}

/**
 * Serviço para validação do perfil do candidato
 */
export class CandidateValidationService {
    /**
     * Verifica se o perfil do candidato está completo para se candidatar a vagas
     * @param candidateId ID do candidato (opcional, usa o ID do localStorage se não fornecido)
     * @returns Promise com o resultado da validação
     */
    static async validateProfile(candidateId?: string): Promise<ValidationResponse> {
        try {
            const id = candidateId || localStorage.getItem('candidate_id');
            
            if (!id) {
                throw new Error('ID do candidato não encontrado');
            }

            const response = await axios.get<ValidationResponse>(
                `${environment.apiUrl}/api/candidate/profile/validation/`,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status !== 200) {
                throw new Error('Erro na validação do perfil');
            }

            // O backend pode retornar status 200 mesmo com perfil inválido
            // Verificar se é um perfil inválido baseado nos dados
            if (response.data && !response.data.is_valid) {
                return response.data; // Retornar dados de validação mesmo se inválido
            }

            return response.data;
        } catch (error: any) {
            // Se for erro 400, tentar extrair informações de validação
            if (error.response?.status === 400) {
                try {
                    const errorData = error.response.data;
                    
                    // Se contém informações de validação, retornar como erro de validação
                    if (errorData.profile_incomplete || errorData.missing_fields) {
                        return {
                            is_valid: false,
                            message: errorData.profile_incomplete || 'Perfil incompleto',
                            missing_fields: errorData.missing_fields || []
                        };
                    }
                } catch (parseError) {
                    // Se não conseguir parsear, continuar com erro genérico
                }
            }

            // Para outros erros, retornar erro genérico
            throw new Error('Erro ao verificar validação do perfil. Tente novamente mais tarde.');
        }
    }

    /**
     * Verifica se o perfil está válido sem gerar exceção
     * @param candidateId ID do candidato (opcional)
     * @returns Promise com boolean indicando se está válido
     */
    static async isProfileValid(candidateId?: string): Promise<boolean> {
        try {
            const validation = await this.validateProfile(candidateId);
            return validation.is_valid;
        } catch (error) {
            console.error('Erro ao verificar validação do perfil:', error);
            return false;
        }
    }

    /**
     * Obtém os campos faltantes do perfil
     * @param candidateId ID do candidato (opcional)
     * @returns Promise com array de campos faltantes
     */
    static async getMissingFields(candidateId?: string): Promise<string[]> {
        try {
            const validation = await this.validateProfile(candidateId);
            return validation.missing_fields || [];
        } catch (error) {
            console.error('Erro ao obter campos faltantes:', error);
            return [];
        }
    }
}
